import React, { useState } from 'react';
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
import { DashboardSidebar } from '../components/dashboard/DashboardSidebar';
import { FolderIcon, FileTextIcon, FileIcon, DownloadIcon, TrashIcon, SearchIcon, PlusIcon, FilterIcon, SortAscIcon, CheckIcon, XIcon, FolderPlusIcon, Share2Icon, MoreHorizontalIcon, EyeIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
export function DocumentsDashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showDropdown, setShowDropdown] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState('');
  // Mock user data
  const [userData] = useState({
    name: 'FATHALLAH Mehdi',
    company: 'Tunipages',
    avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
    email: 'mehdi.fathallah@tunipages.com'
  });
  // Mock documents data
  const documents = [{
    id: 'doc1',
    name: "Cahier des charges - Appel d'offres système informatique",
    type: 'pdf',
    size: '2.4 MB',
    date: '2023-05-15',
    folder: "Appels d'offres",
    shared: false
  }, {
    id: 'doc2',
    name: 'Réponse - Projet infrastructure réseau',
    type: 'docx',
    size: '1.8 MB',
    date: '2023-05-10',
    folder: 'Réponses',
    shared: true
  }, {
    id: 'doc3',
    name: 'Présentation commerciale - Services cloud',
    type: 'pptx',
    size: '4.2 MB',
    date: '2023-04-28',
    folder: 'Présentations',
    shared: false
  }, {
    id: 'doc4',
    name: 'Devis - Projet migration cloud',
    type: 'xlsx',
    size: '1.1 MB',
    date: '2023-04-22',
    folder: 'Devis',
    shared: false
  }, {
    id: 'doc5',
    name: 'Contrat - Maintenance informatique',
    type: 'pdf',
    size: '3.5 MB',
    date: '2023-04-15',
    folder: 'Contrats',
    shared: true
  }, {
    id: 'doc6',
    name: 'Annexe technique - Projet réseau',
    type: 'pdf',
    size: '2.8 MB',
    date: '2023-04-10',
    folder: "Appels d'offres",
    shared: false
  }, {
    id: 'doc7',
    name: 'Planning - Déploiement phase 1',
    type: 'xlsx',
    size: '0.9 MB',
    date: '2023-03-28',
    folder: 'Plannings',
    shared: false
  }, {
    id: 'doc8',
    name: 'Rapport mensuel - Mars 2023',
    type: 'pdf',
    size: '1.7 MB',
    date: '2023-03-31',
    folder: 'Rapports',
    shared: false
  }];
  // Mock folders data
  const folders = [{
    id: 'folder1',
    name: "Appels d'offres",
    count: 12
  }, {
    id: 'folder2',
    name: 'Réponses',
    count: 8
  }, {
    id: 'folder3',
    name: 'Présentations',
    count: 5
  }, {
    id: 'folder4',
    name: 'Devis',
    count: 7
  }, {
    id: 'folder5',
    name: 'Contrats',
    count: 4
  }, {
    id: 'folder6',
    name: 'Plannings',
    count: 3
  }, {
    id: 'folder7',
    name: 'Rapports',
    count: 6
  }];
  // Get icon based on file type
  const getFileIcon = (type: string) => {
    switch (type) {
      case 'pdf':
        return <div className="text-red-500" size={20} data-id="element-2249" />;
      case 'docx':
        return <FileTextIcon className="text-blue-500" size={20} data-id="element-2250" />;
      case 'xlsx':
        return <FileIcon className="text-green-500" size={20} data-id="element-2251" />;
      case 'pptx':
        return <FileIcon className="text-orange-500" size={20} data-id="element-2252" />;
      default:
        return <FileIcon className="text-gray-500" size={20} data-id="element-2253" />;
    }
  };
  // Format date for display
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };
  // Toggle document selection
  const toggleDocumentSelection = (id: string) => {
    if (selectedDocuments.includes(id)) {
      setSelectedDocuments(selectedDocuments.filter(docId => docId !== id));
    } else {
      setSelectedDocuments([...selectedDocuments, id]);
    }
  };
  // Select all documents
  const selectAllDocuments = () => {
    if (selectedDocuments.length === filteredDocuments.length) {
      setSelectedDocuments([]);
    } else {
      setSelectedDocuments(filteredDocuments.map(doc => doc.id));
    }
  };
  // Handle document action
  const handleDocumentAction = (action: string, docId?: string) => {
    const docsToAction = docId ? [docId] : selectedDocuments;
    switch (action) {
      case 'download':
        console.log(`Downloading documents: ${docsToAction.join(', ')}`);
        setSuccessMessage(`${docsToAction.length} document(s) téléchargé(s) avec succès`);
        break;
      case 'delete':
        console.log(`Deleting documents: ${docsToAction.join(', ')}`);
        setSuccessMessage(`${docsToAction.length} document(s) supprimé(s) avec succès`);
        setSelectedDocuments([]);
        break;
      case 'share':
        console.log(`Sharing documents: ${docsToAction.join(', ')}`);
        setSuccessMessage(`Lien de partage créé pour ${docsToAction.length} document(s)`);
        break;
      default:
        break;
    }
    setShowDropdown(null);
    // Clear success message after a delay
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };
  // Filter documents based on search query
  const filteredDocuments = documents.filter(doc => doc.name.toLowerCase().includes(searchQuery.toLowerCase()) || doc.folder.toLowerCase().includes(searchQuery.toLowerCase()));
  // Sort documents
  const sortedDocuments = [...filteredDocuments].sort((a, b) => {
    if (sortBy === 'name') {
      return sortOrder === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
    } else if (sortBy === 'size') {
      const sizeA = parseFloat(a.size.split(' ')[0]);
      const sizeB = parseFloat(b.size.split(' ')[0]);
      return sortOrder === 'asc' ? sizeA - sizeB : sizeB - sizeA;
    } else {
      // Sort by date
      return sortOrder === 'asc' ? new Date(a.date).getTime() - new Date(b.date).getTime() : new Date(b.date).getTime() - new Date(a.date).getTime();
    }
  });
  return <div className="min-h-screen bg-gray-50" data-id="element-2254">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && <div className="fixed inset-0 bg-gray-800 bg-opacity-50 z-20 md:hidden" onClick={() => setSidebarOpen(false)} data-id="element-2255" />}
      {/* Sidebar */}
      <DashboardSidebar open={sidebarOpen} setOpen={setSidebarOpen} data-id="element-2256" />
      {/* Main content */}
      <div className="md:pl-64" data-id="element-2257">
        {/* Header */}
        <DashboardHeader user={userData} onMenuClick={() => setSidebarOpen(true)} data-id="element-2258" />
        {/* Main content */}
        <main className="py-6 px-4 sm:px-6 lg:px-8" data-id="element-2259">
          {/* Page header */}
          <div className="mb-6" data-id="element-2260">
            <div className="flex items-center justify-between mb-4" data-id="element-2261">
              <div className="flex items-center" data-id="element-2262">
                <FolderIcon size={24} className="text-blue-600 mr-3" data-id="element-2263" />
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-2264">
                  Mes documents
                </h1>
              </div>
              <Button variant="primary" className="flex items-center shadow-md shadow-yellow-500/20" data-id="element-2265">
                <PlusIcon size={16} className="mr-2" data-id="element-2266" />
                Importer un document
              </Button>
            </div>
            <p className="text-gray-600" data-id="element-2267">
              Gérez tous vos documents liés aux appels d'offres
            </p>
          </div>

          {/* Success message */}
          {successMessage && <div className="mb-6 bg-green-100 border border-green-200 text-green-800 rounded-lg p-4 flex items-start" data-id="element-2268">
              <CheckIcon size={20} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-2269" />
              <div data-id="element-2270">
                <p className="font-medium" data-id="element-2271">{successMessage}</p>
              </div>
              <button onClick={() => setSuccessMessage('')} className="ml-auto text-green-500 hover:text-green-700" data-id="element-2272">
                <XIcon size={16} data-id="element-2273" />
              </button>
            </div>}
          <div className="flex flex-col md:flex-row gap-6" data-id="element-2274">
            {/* Folders sidebar */}
            <div className="w-full md:w-64 flex-shrink-0" data-id="element-2275">
              <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100" data-id="element-2276">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between" data-id="element-2277">
                  <h2 className="font-medium text-gray-900" data-id="element-2278">Dossiers</h2>
                  <button className="text-gray-500 hover:text-blue-600" data-id="element-2279">
                    <FolderPlusIcon size={16} data-id="element-2280" />
                  </button>
                </div>
                <div className="p-2" data-id="element-2281">
                  <button className="w-full flex items-center px-3 py-2 rounded-md text-left text-gray-900 hover:bg-blue-50 font-medium" data-id="element-2282">
                    <FolderIcon size={16} className="text-blue-600 mr-2" data-id="element-2283" />
                    <span data-id="element-2284">Tous les documents</span>
                    <span className="ml-auto bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full" data-id="element-2285">
                      {documents.length}
                    </span>
                  </button>
                  {folders.map(folder => <button key={folder.id} className="w-full flex items-center px-3 py-2 rounded-md text-left text-gray-700 hover:bg-blue-50" data-id="element-2286">
                      <FolderIcon size={16} className="text-gray-400 mr-2" data-id="element-2287" />
                      <span data-id="element-2288">{folder.name}</span>
                      <span className="ml-auto bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full" data-id="element-2289">
                        {folder.count}
                      </span>
                    </button>)}
                </div>
              </div>
              <div className="mt-6 bg-white rounded-lg shadow-md overflow-hidden border border-gray-100" data-id="element-2290">
                <div className="p-4 border-b border-gray-200" data-id="element-2291">
                  <h2 className="font-medium text-gray-900" data-id="element-2292">Stockage</h2>
                </div>
                <div className="p-4" data-id="element-2293">
                  <div className="mb-2 flex justify-between text-sm" data-id="element-2294">
                    <span className="text-gray-600" data-id="element-2295">Espace utilisé</span>
                    <span className="font-medium text-gray-900" data-id="element-2296">
                      45.8 GB / 100 GB
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-2297">
                    <div className="bg-blue-600 h-2.5 rounded-full" style={{
                    width: '45.8%'
                  }} data-id="element-2298"></div>
                  </div>
                  <p className="mt-2 text-xs text-gray-500" data-id="element-2299">
                    Votre abonnement vous donne accès à 100 GB de stockage.
                  </p>
                </div>
              </div>
            </div>

            {/* Documents list */}
            <div className="flex-1" data-id="element-2300">
              <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100" data-id="element-2301">
                {/* Search and filters */}
                <div className="p-4 border-b border-gray-200" data-id="element-2302">
                  <div className="flex flex-col sm:flex-row gap-4" data-id="element-2303">
                    <div className="relative flex-grow" data-id="element-2304">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2305">
                        <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-2306" />
                      </div>
                      <input type="text" className="block w-full bg-gray-50 border border-gray-300 rounded-md py-2 pl-10 pr-3 text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Rechercher un document..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-2307" />
                    </div>
                    <div className="flex space-x-2" data-id="element-2308">
                      <div className="relative" data-id="element-2309">
                        <button className="flex items-center px-3 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50" onClick={() => setShowDropdown(showDropdown === 'sort' ? null : 'sort')} data-id="element-2310">
                          <SortAscIcon size={16} className="mr-2" data-id="element-2311" />
                          Trier
                        </button>
                        {showDropdown === 'sort' && <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10" data-id="element-2312">
                            <div className="py-1" data-id="element-2313">
                              <button className={`block px-4 py-2 text-sm text-left w-full ${sortBy === 'date' && sortOrder === 'desc' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`} onClick={() => {
                            setSortBy('date');
                            setSortOrder('desc');
                            setShowDropdown(null);
                          }} data-id="element-2314">
                                Date (plus récent d'abord)
                              </button>
                              <button className={`block px-4 py-2 text-sm text-left w-full ${sortBy === 'date' && sortOrder === 'asc' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`} onClick={() => {
                            setSortBy('date');
                            setSortOrder('asc');
                            setShowDropdown(null);
                          }} data-id="element-2315">
                                Date (plus ancien d'abord)
                              </button>
                              <button className={`block px-4 py-2 text-sm text-left w-full ${sortBy === 'name' && sortOrder === 'asc' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`} onClick={() => {
                            setSortBy('name');
                            setSortOrder('asc');
                            setShowDropdown(null);
                          }} data-id="element-2316">
                                Nom (A-Z)
                              </button>
                              <button className={`block px-4 py-2 text-sm text-left w-full ${sortBy === 'name' && sortOrder === 'desc' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`} onClick={() => {
                            setSortBy('name');
                            setSortOrder('desc');
                            setShowDropdown(null);
                          }} data-id="element-2317">
                                Nom (Z-A)
                              </button>
                              <button className={`block px-4 py-2 text-sm text-left w-full ${sortBy === 'size' && sortOrder === 'desc' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`} onClick={() => {
                            setSortBy('size');
                            setSortOrder('desc');
                            setShowDropdown(null);
                          }} data-id="element-2318">
                                Taille (plus grand d'abord)
                              </button>
                              <button className={`block px-4 py-2 text-sm text-left w-full ${sortBy === 'size' && sortOrder === 'asc' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`} onClick={() => {
                            setSortBy('size');
                            setSortOrder('asc');
                            setShowDropdown(null);
                          }} data-id="element-2319">
                                Taille (plus petit d'abord)
                              </button>
                            </div>
                          </div>}
                      </div>
                      <button className="flex items-center px-3 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-2320">
                        <FilterIcon size={16} className="mr-2" data-id="element-2321" />
                        Filtrer
                      </button>
                    </div>
                  </div>
                </div>

                {/* Documents table */}
                {sortedDocuments.length === 0 ? <div className="text-center py-12" data-id="element-2322">
                    <FileIcon size={48} className="mx-auto text-gray-400 mb-4" data-id="element-2323" />
                    <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-2324">
                      Aucun document trouvé
                    </h3>

                    <p className="text-gray-500" data-id="element-2325">
                      {searchQuery ? "Essayez avec d'autres termes de recherche" : 'Commencez par importer des documents'}
                    </p>
                  </div> : <div className="overflow-x-auto" data-id="element-2326">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-2327">
                      <thead className="bg-gray-50" data-id="element-2328">
                        <tr data-id="element-2329">
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8" data-id="element-2330">
                            <div className="flex items-center" data-id="element-2331">
                              <input type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded" checked={selectedDocuments.length === filteredDocuments.length && filteredDocuments.length > 0} onChange={selectAllDocuments} data-id="element-2332" />
                            </div>
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2333">
                            Nom
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2334">
                            Dossier
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2335">
                            Date
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2336">
                            Taille
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2337">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-2338">
                        {sortedDocuments.map(document => <tr key={document.id} className="hover:bg-gray-50" data-id="element-2339">
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-2340">
                              <input type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded" checked={selectedDocuments.includes(document.id)} onChange={() => toggleDocumentSelection(document.id)} data-id="element-2341" />
                            </td>
                            <td className="px-6 py-4" data-id="element-2342">
                              <div className="flex items-center" data-id="element-2343">
                                <div className="flex-shrink-0 mr-3" data-id="element-2344">
                                  {getFileIcon(document.type)}
                                </div>
                                <div className="text-sm font-medium text-gray-900" data-id="element-2345">
                                  {document.name}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-2346">
                              <div className="text-sm text-gray-500" data-id="element-2347">
                                {document.folder}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-2348">
                              <div className="text-sm text-gray-500" data-id="element-2349">
                                {formatDate(document.date)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-2350">
                              <div className="text-sm text-gray-500" data-id="element-2351">
                                {document.size}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2352">
                              <div className="flex items-center space-x-2" data-id="element-2353">
                                <button className="text-gray-500 hover:text-blue-600" title="Télécharger" onClick={() => handleDocumentAction('download', document.id)} data-id="element-2354">
                                  <DownloadIcon size={16} data-id="element-2355" />
                                </button>
                                <button className="text-gray-500 hover:text-blue-600" title="Visualiser" data-id="element-2356">
                                  <EyeIcon size={16} data-id="element-2357" />
                                </button>
                                <button className="text-gray-500 hover:text-blue-600" title="Partager" onClick={() => handleDocumentAction('share', document.id)} data-id="element-2358">
                                  <Share2Icon size={16} data-id="element-2359" />
                                </button>
                                <div className="relative" data-id="element-2360">
                                  <button className="text-gray-500 hover:text-blue-600" onClick={() => setShowDropdown(showDropdown === document.id ? null : document.id)} data-id="element-2361">
                                    <MoreHorizontalIcon size={16} data-id="element-2362" />
                                  </button>
                                  {showDropdown === document.id && <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10" data-id="element-2363">
                                      <div className="py-1" data-id="element-2364">
                                        <button className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left" onClick={() => handleDocumentAction('download', document.id)} data-id="element-2365">
                                          <DownloadIcon size={16} className="mr-3 text-gray-500" data-id="element-2366" />
                                          Télécharger
                                        </button>
                                        <button className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left" data-id="element-2367">
                                          <EyeIcon size={16} className="mr-3 text-gray-500" data-id="element-2368" />
                                          Visualiser
                                        </button>
                                        <button className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left" onClick={() => handleDocumentAction('share', document.id)} data-id="element-2369">
                                          <Share2Icon size={16} className="mr-3 text-gray-500" data-id="element-2370" />
                                          Partager
                                        </button>
                                        <button className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left" onClick={() => handleDocumentAction('delete', document.id)} data-id="element-2371">
                                          <TrashIcon size={16} className="mr-3 text-red-500" data-id="element-2372" />
                                          Supprimer
                                        </button>
                                      </div>
                                    </div>}
                                </div>
                              </div>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>}
                {/* Bulk actions */}
                {selectedDocuments.length > 0 && <div className="px-6 py-4 bg-gray-50 border-t border-gray-200" data-id="element-2373">
                    <div className="flex items-center justify-between" data-id="element-2374">
                      <div className="text-sm text-gray-700" data-id="element-2375">
                        {selectedDocuments.length} document
                        {selectedDocuments.length > 1 ? 's' : ''} sélectionné
                        {selectedDocuments.length > 1 ? 's' : ''}
                      </div>
                      <div className="flex space-x-2" data-id="element-2376">
                        <Button variant="outline" size="sm" className="flex items-center" onClick={() => handleDocumentAction('download')} data-id="element-2377">
                          <DownloadIcon size={14} className="mr-1" data-id="element-2378" />
                          Télécharger
                        </Button>
                        <Button variant="outline" size="sm" className="flex items-center" onClick={() => handleDocumentAction('share')} data-id="element-2379">
                          <Share2Icon size={14} className="mr-1" data-id="element-2380" />
                          Partager
                        </Button>
                        <Button variant="outline" size="sm" className="flex items-center text-red-600 border-red-600 hover:bg-red-50" onClick={() => handleDocumentAction('delete')} data-id="element-2381">
                          <TrashIcon size={14} className="mr-1" data-id="element-2382" />
                          Supprimer
                        </Button>
                      </div>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>;
}